import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, TrendingUp, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL (http/https), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative path, prepend API_URL
  if (url.startsWith('/api/')) {
    return `${API_URL}${url}`;
  }
  // Otherwise return as is
  return url;
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [bestsellers, setBestsellers] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const lang = i18n.language;
        const [bestsellersRes, newRes, catsRes] = await Promise.all([
          axios.get(`${API_URL}/api/products?is_bestseller=true&limit=4&lang=${lang}`),
          axios.get(`${API_URL}/api/products?is_new=true&limit=4&lang=${lang}`),
          axios.get(`${API_URL}/api/categories?lang=${lang}`)
        ]);
        setBestsellers(bestsellersRes.data);
        setNewProducts(newRes.data);
        setCategories(catsRes.data);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [i18n.language]);

  return (
    <div className="min-h-screen pt-16 sm:pt-20 bg-brand-cream" data-testid="home-page">
      {/* Hero Section - Elegant Black & Gold */}
      <section className="relative h-[80vh] sm:h-[90vh] overflow-hidden bg-brand-black">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/hero-bg.jpg"
            alt="ALINA ELEGANCE"
            className="w-full h-full object-cover opacity-70"
          />
          {/* Elegant dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/80 to-brand-black/60" />
        </div>
        
        {/* Decorative gold elements */}
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full border border-brand-gold/20 animate-float hidden xl:block" />
        <div className="absolute bottom-40 right-40 w-32 h-32 rounded-full border border-brand-gold/10 animate-float hidden xl:block" style={{animationDelay: '1s'}} />
        
        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <span className="font-body text-brand-gold text-xs sm:text-sm tracking-[0.3em] uppercase mb-4 block flex items-center gap-2">
              <Star className="h-3 w-3 sm:h-4 sm:w-4" /> {t('home.collectionExclusive')}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-brand-white leading-tight mb-6 font-bold">
              {t('home.heroTitle')}<br />
              <span className="text-brand-gold italic">{t('home.heroTitleHighlight')}</span>
            </h1>
            <p className="text-brand-white/60 text-sm sm:text-base lg:text-lg mb-8 max-w-md font-body font-light leading-relaxed">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <Button className="w-full sm:w-auto bg-brand-gold text-brand-black hover:bg-brand-gold/90 px-8 py-6 text-sm font-medium uppercase tracking-widest rounded-none transition-all duration-300" data-testid="shop-now-btn">
                  {t('home.discover')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/products?is_new=true">
                <Button variant="outline" className="w-full sm:w-auto border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-black px-8 py-6 text-sm uppercase tracking-widest rounded-none transition-all duration-300">
                  {t('nav.newArrivals')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Bottom gold line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
      </section>

      {/* Categories Section - White/Cream with Gold accents */}
      <section className="py-16 sm:py-20 lg:py-28 bg-brand-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <span className="font-body text-brand-gold text-xs tracking-[0.3em] uppercase">{t('home.exploreCollection')}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-black mt-3 font-bold">{t('nav.categories')}</h2>
            <div className="w-20 h-px bg-brand-gold mx-auto mt-4" />
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {categories.map((cat, idx) => (
              <motion.div
                key={cat.category_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link 
                  to={`/products?category=${cat.slug}`}
                  className="group block relative overflow-hidden aspect-[3/4] border-2 border-brand-gold/20 hover:border-brand-gold transition-all duration-300 bg-brand-cream"
                >
                  <img 
                    src={getImageUrl(cat.image) || `https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&h=500&fit=crop`} 
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&h=500&fit=crop'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/40 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-5">
                    <h3 className="font-heading text-brand-white text-base sm:text-lg font-semibold uppercase tracking-wide drop-shadow-lg group-hover:text-brand-gold transition-colors duration-300">{cat.name}</h3>
                    <span className="text-brand-gold text-xs uppercase tracking-wider mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-body flex items-center gap-2">
                      {t('home.discover')} <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                  {/* Gold corner accents on hover */}
                  <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-brand-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-brand-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers Section */}
      <section className="py-16 sm:py-20 lg:py-28 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12"
          >
            <div>
              <motion.span 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="font-body text-brand-gold text-sm tracking-[0.2em] uppercase flex items-center gap-2 font-semibold"
              >
                <TrendingUp className="h-5 w-5" /> {t('home.mostPopular')}
              </motion.span>
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-black mt-3 font-bold"
              >
                {t('nav.bestsellers')}
              </motion.h2>
              <div className="w-16 h-1 bg-brand-gold mt-3" />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/products?filter=bestseller" className="text-brand-black hover:text-brand-gold transition-colors font-body text-sm tracking-wide flex items-center gap-2 font-medium border-b-2 border-brand-gold pb-1">
                {t('home.viewAll')} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {bestsellers.map((product, idx) => (
                <motion.div
                  key={product.product_id}
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15, duration: 0.5 }}
                  whileHover={{ y: -8 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Elegant Banner */}
      <section className="py-20 sm:py-28 bg-brand-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border border-brand-gold rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border border-brand-gold rounded-full" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <span className="text-brand-gold text-xs tracking-[0.3em] uppercase font-body">{t('home.collectionExclusive')}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-white mt-4 mb-6 leading-tight font-bold">
              {t('home.premiumDescription').split(',')[0]}<br />
              <span className="text-brand-gold italic">{t('home.heroTitleHighlight')}</span>
            </h2>
            <p className="text-brand-white/50 mb-8 max-w-xl mx-auto font-body font-light">
              {t('home.premiumDescription')}
            </p>
            <Link to="/products">
              <Button className="bg-brand-gold text-brand-black hover:bg-brand-white px-10 py-6 text-sm uppercase tracking-widest rounded-none font-medium transition-all duration-300">
                {t('home.discover')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-16 sm:py-20 lg:py-28 bg-brand-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12"
          >
            <div>
              <motion.span 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="font-body text-brand-gold text-sm tracking-[0.2em] uppercase flex items-center gap-2 font-semibold"
              >
                <Sparkles className="h-5 w-5" /> {t('home.justArrived')}
              </motion.span>
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-black mt-3 font-bold"
              >
                {t('nav.newArrivals')}
              </motion.h2>
              <div className="w-16 h-1 bg-brand-gold mt-3" />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/products?is_new=true" className="text-brand-black hover:text-brand-gold transition-colors font-body text-sm tracking-wide flex items-center gap-2 font-medium border-b-2 border-brand-gold pb-1">
                {t('home.viewAll')} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {newProducts.map((product, idx) => (
                <motion.div
                  key={product.product_id}
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15, duration: 0.5 }}
                  whileHover={{ y: -8 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 sm:py-20 bg-brand-white border-t border-brand-gold/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { title: t('home.delivery'), desc: t('home.deliveryDesc') },
              { title: t('home.quality'), desc: t('home.qualityDesc') },
              { title: t('home.support'), desc: t('home.supportDesc') },
              { title: t('footer.returns'), desc: t('products.freeReturns') },
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-6"
              >
                <div className="w-12 h-12 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-5 w-5 text-brand-gold" />
                </div>
                <h3 className="font-heading text-brand-dark text-lg mb-1">{item.title}</h3>
                <p className="text-brand-dark/50 text-sm font-body">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
